import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { RefObject, useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import FilterMenu from "views/components/FilterMenu";
import { useCustomMetadata } from "hooks/useCustomMetadata";

import { FilterMenuItemType, Position } from "types";
import { isCustomMetadata } from "helpers/utility/customMetadataHelpers";

import styles from "./style.module.scss";
import { FiltersIcon } from "../Icons";
import VLTooltip from "views/uikit/VLTooltip";
import VLPopover from "views/uikit/VLPopover";

export interface AddFilterProps {
  rowRef: RefObject<HTMLDivElement>;
  actionsRef?: RefObject<HTMLDivElement>;
}

export const MIN_SPACE_RIGHT_OF_ROW_PX = 500;

export default function AddFilter({ rowRef, actionsRef }: AddFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef<HTMLDivElement>(null);

  const { datasetId } = useParams();
  const dispatch = useDispatch();

  const { data: customMetadataData } = useCustomMetadata({
    datasetId: datasetId!,
  });

  const computeBtnPosition = useCallback((): Position | null => {
    if (!btnRef.current) return null;
    const rect = btnRef.current.getBoundingClientRect();
    const actions = actionsRef?.current;
    const row = rowRef.current;

    const anchorRight = actions
      ? actions.getBoundingClientRect().right
      : row
        ? row.getBoundingClientRect().right
        : rect.right;

    const spaceOnRightOfRow = window.innerWidth - anchorRight;

    const isRightAligned = spaceOnRightOfRow >= MIN_SPACE_RIGHT_OF_ROW_PX;

    return {
      top: rect.bottom + window.scrollY + 4,
      left: (isRightAligned ? rect.left : rect.right) + window.scrollX,
      align: isRightAligned ? "start" : "end",
    };
  }, [rowRef, actionsRef]);

  const handleFilterSelection = (type: FilterMenuItemType) => {
    // Handle custom metadata filters
    const position = computeBtnPosition();
    setIsOpen(false);

    if (isCustomMetadata(type)) {
      if (position) {
        dispatch(setSelectedQueryModal({ type, position }));
      }
      return;
    }

    dispatch(
      setSelectedQueryModal({
        type,
        position: position || undefined,
      }),
    );
  };

  return (
    <VLPopover
      trigger="click"
      placement={`bottomLeft`}
      open={isOpen}
      onOpenChange={setIsOpen}
      arrow={false}
      overlayClassName={styles.addFilterPopover}
      content={
        <FilterMenu
          onSelect={handleFilterSelection}
          datasetId={datasetId!}
          customMetadataData={customMetadataData}
        />
      }
      styles={{ container: { padding: 0 } }}
    >
      <div data-testid="add-filter-popover-trigger">
        <div
          className={styles.addFilterBtn}
          data-testid="add-filter-btn"
          ref={btnRef}
        >
          <VLTooltip title="Add Filter" forceHide={isOpen}>
            <div className={styles.addFilterIcon} data-testid="add-filter-icon">
              <FiltersIcon />
            </div>
          </VLTooltip>
        </div>
      </div>
    </VLPopover>
  );
}
